// English dictionary — the key contract. pl.ts must have exactly the same shape.
// Placeholders use {name}; plural messages are objects with one/other.

export const en = {
  studio: {
  "openReports": "Open reports"
},
  reports: {
  "tab": "Reports",
  "title": "Cash flow report",
  "hint": "Booked income and spending, with refunds deducted. Transfers and pending payments are excluded. Split transactions use their individual categories; currencies stay separate.",
  "category": "Category",
  "month": "Month",
  "merchant": "Merchant",
  "groupBy": "Group report by",
  "unknownMerchant": "No merchant",
  "count": "Transactions",
  "netSpending": "Spending after refunds",
  "incomeShort": "in {amount}",
  "spendingShort": "out {amount}",
  "exportReport": "Export report",
  "exportTransactions": "Export transactions",
  "empty": "No booked income or spending matches these filters.",
  "saveView": "Save filters",
  "viewName": "View name",
  "viewHint": "Saved for this household on this device. “Selected period” follows the period at the top; a custom range stays fixed.",
  "viewError": "Use a unique name (up to 60 characters) and a valid date range. You can save up to 20 views.",
  "removeView": "Remove saved filters: {name}",
  "date": "Date",
  "status": "Status",
  "kind": "Type",
  "split": "Split categories",
  "range": "Date range",
  "selectedPeriod": "Selected period",
  "customRange": "Custom dates",
  "from": "From",
  "to": "To",
  "allStatuses": "All statuses",
  "booked": "Booked",
  "pending": "Pending",
  "dateError": "Choose a valid start and end date. The start must not be after the end.",
  "loadError": "Could not load transactions. Totals may be incomplete.",
  "totalsHint": "Totals include booked entries only. Refunds reduce spending; transfers do not count as income or spending. CSV transactions contain the full amount of each matching entry."
},
  goals: {
    title: 'Savings goals',
    subtitle: 'Give your savings a purpose and a monthly plan.',
    add: 'New goal',
    edit: 'Edit goal',
    name: 'Goal name',
    nameExample: 'Emergency fund, holiday, deposit…',
    target: 'Target amount',
    currency: 'Currency',
    source: 'Track progress with',
    manual: 'Manual savings pot',
    manualHint: 'Enter the amount set aside. Updating it does not create a transaction.',
    accountHint: 'The full current balance funds this goal. Each account can fund one active goal.',
    unavailable: 'Choose another account: the linked account is unavailable or already assigned.',
    savedAmount: 'Already saved',
    monthly: 'Monthly plan',
    deadline: 'Target date (optional)',
    deadlineShort: 'Target date',
    projectionHint:
      'Estimates assume one deposit each month, starting a month from today, with no investment returns. This plan does not move money.',
    archive: 'Archive',
    restore: 'Restore goal',
    active: 'Active goals',
    archived: 'Archive',
    saved: 'Goal saved on this device; synchronisation requested.',
    invalid: 'Check the name, amounts, currency, account and date. The target must be above zero.',
    saveError: 'Could not save the goal. Try again.',
    remaining: 'Still to save',
    estimated: 'Estimated completion',
    needed: 'To meet your date:',
    perMonth: ' / month',
    reached: 'Target reached',
    behind: 'Plan needs attention',
    inProgress: 'In progress',
    deadlineSoon:
      'There is no monthly deposit before this date. Make a one-off contribution or adjust the date.',
    empty: 'What are you saving for?',
    emptyHint: 'Set a target, link a savings account and see what to put aside each month.',
    emptyArchive: 'No archived goals',
    archiveHint: 'Archived goals keep their last recorded progress and can be restored.',
    loadError: 'Could not load savings goals.',
  },
  app: {
    name: 'Finance Dashboard',
  },
  common: {
    todayAt: 'today {time}',
    tomorrowAt: 'tomorrow {time}',
    yesterdayAt: 'yesterday {time}',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    add: 'Add',
    close: 'Close',
    retry: 'Try again',
    search: 'Search',
    loading: 'Loading…',
    more: 'More',
  },
  nav: {
    dashboard: 'Dashboard',
    accounts: 'Accounts',
    transactions: 'Transactions',
    cashFlow: 'Cash flow',
    budgets: 'Budgets',
    loan: 'Loan',
    loans: 'Loans',
    groupSpending: 'Spending',
    groupWealth: 'Wealth',
    groupShared: 'Shared money',
    countInPeriod: 'In the chosen period',
    people: 'People',
    settlements: 'Settlements',
    recurring: 'Recurring',
    goals: 'Goals',
    netWorth: 'Net worth',
    simulation: "Calculators",
    savings: 'Savings & investments',
    reports: 'Reports',
    settings: 'Settings',
    home: 'Home',
  },
  period: {
    week: 'Week',
    month: 'Month',
    year: 'Year',
    previous: 'Previous period',
    next: 'Next period',
    vsPreviousWeek: 'vs previous week',
    vsPreviousMonth: 'vs previous month',
    vsPreviousYear: 'vs previous year',
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'Private finance dashboard for its owner.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    signOut: 'Sign out',
    invalidCredentials: 'Email or password is incorrect.',
    sessionExpired: 'Your session has expired. Please sign in again.',
  },
  onboarding: {
    title: 'Set up your household',
    language: 'Language',
    householdName: 'Household name',
    householdNamePlaceholder: 'Home',
    baseCurrency: 'Base currency',
    baseCurrencyHint: 'Totals, net worth and reports are shown in this currency.',
    people: 'People',
    peopleHint: 'Add everyone who shares costs. Only you sign in — people do not need accounts.',
    personName: 'Name',
    addPerson: 'Add person',
    thisIsMe: 'This is me',
    defaultSplit: 'How do you usually split shared costs?',
    ownData: 'My household',
    demoData: 'Start with demo data',
    demoDataHint: 'A realistic sample household you can explore and delete later.',
    finish: 'Go to dashboard',
  },
  heading: {
    transactions: 'Everything that came in and went out in the chosen period',
    reports: 'Spending and income reports for the chosen period',
    upcoming: 'The balance ahead and the payments still to come',
    recurring: 'Regular payments that fall in the chosen period',
    budgets: 'What is left of the budgets in the chosen period',
    settlements: 'Shared costs and who pays back whom',
    accounts: 'Your accounts, cards and their balances',
    loans: 'Loans and what is left to repay',
    loan: 'The repayment schedule and what is left',
    savings: 'Savings, goals and investments',
    netWorth: 'Net worth and how it changes',
    simulation: "A year of bills simulated, and a salary calculator",
    people: 'People in the household and how costs are split',
    settings: 'App and household settings',
  },
  dashboard: {
    layout: {
      edit: 'Arrange dashboard',
      hint: 'Drag the cards, or move them with the arrows. The same layout on all your devices.',
      hintBoard: 'Drag a card to other cells of the grid; its right edge, bottom edge or corner change its size a whole cell at a time — each card has a smallest and a largest size, and its content fits itself to it. The same layout on all your devices.',
      reset: 'Default layout',
      done: 'Done',
      up: 'Move up',
      down: 'Move down',
      move: 'Move (arrows)',
      resize: 'Resize (arrows)',
      hide: 'Hide',
      hidden: 'Hidden cards:',
    },
    widgets: {
      metrics: 'Period summary',
      overview: 'Net worth and forecast',
      flow: 'Cash flow',
      settlement: 'Settle up',
      recent: 'Recent transactions',
      upcoming: 'Upcoming payments',
      heatmap: 'Spending map',
      changes: 'Changes in spending',
      savings: 'Savings and investments',
    },
    loadError: 'Some data could not be loaded',
    loadErrorHint: 'Your summary may be out of date. Try loading the data again.',
    incompleteTotals: 'Some transactions are missing from this summary',
    incompleteTotalsHint: '{count} transactions in this or the previous period have no {currency} conversion. Totals and comparisons exclude these entries.',
    reviewMissing: 'Review excluded transactions ({count})',
    historyRange: 'Balance history',
    skipToContent: 'Skip to main content',
    title: 'Dashboard',
    income: 'Income',
    expenses: 'Spending',
    saved: 'Put aside',
    cashFlow: 'Cash flow',
    recentTransactions: 'Recent transactions',
    settlement: 'Settle up',
    savingsCard: 'Savings & investments',
    viewAll: 'View all',
    spendingChangesEmpty: 'Nothing to compare yet.',
    newCategory: 'new',
    flowFromBalance: 'From balances',
    flowOther: { one: 'Other ({count} category)', other: 'Other ({count} categories)' },
    flowOtherIncome: 'Other income',
    flowSavings: 'Savings & investments',
    flowSurplus: 'Left in the accounts',
    flowStayedOn: 'Left on {account}',
    flowElsewhere: 'Other outflows',
    flowShareOfIncome: '{share} of income',
    flowDrill: 'Breakdown',
    flowCaptionCategories: 'Categories',
    flowPaceShort: 'pace {amount}',
    flowBackCaption: 'Back to',
    flowBackHint: 'Back to the whole',
    flowSide: 'Side of the flow',
    flowWhereTo: 'Where it goes',
    flowWhereFrom: 'Income',
    flowCrumbs: 'Breakdown level',
    flowAllSpending: 'All spending',
    flowAllIncome: 'All income',
    flowGroupLabel: 'Group',
    flowEnter: 'Break it down',
    flowOpenEntries: 'Show the entries',
    flowDrillHint: 'Point at a tile to see its band. Click a group to break it down.',
    flowDrillHintIn: 'Click a category to see its entries.',
    flowEmpty: 'No income or spending in this period yet.',
    changeWeek: 'this week',
    changeMonth: 'this month',
    changeYear: 'this year',
    heroChart: 'Net worth over time',
    netFlow: 'Income less spending',
    netFlowHint: 'How much this period’s income exceeded its spending (payments still pending included). What went to savings is not spending, so it is not taken off here.',
    freshBank: 'Bank: read {last}',
    freshBankNext: 'Bank: read {last}, next {next}',
    freshInvest: 'Trading 212: {time}',
  },
  heatmap: {
    days: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    title: 'Spending heatmap',
    subtitle: 'Last 12 months, day by day',
    lastWeeks: { one: 'Last {count} week, day by day', other: 'Last {count} weeks, day by day' },
    less: 'Less',
    more: 'More',
    dailyAverage: 'Daily average',
    biggestDay: 'Biggest day',
    daysWithout: 'No-spend days',
    noSpending: 'No spending',
    openDay: 'Show transactions from {date}',
    summary: 'Spending per day over the last 12 months. Biggest day: {date}, {amount}.',
  },
  loans: {
    intro: 'All your loans in one place. Click a loan to see the details: balance over time, interest and overpaying.',
    add: 'Add a loan',
    empty: 'No loans yet',
    emptyHint: 'Add a loan to see its payments, interest and payoff date.',
    totalPayable: 'Still to pay',
    count: { one: '{count} loan', other: '{count} loans' },
    monthly: 'Monthly payments',
    details: 'Details',
    suggestionsWaiting: 'A repayment was found that can be added as a loan',
    spotOverline: 'Loans · to repay',
    spotPaid: 'Repaid',
    spotLeft: 'Left',
    spotNext: 'Next payment, {date}',
    spotEnd: 'Paid off',
    spotInterest: 'Interest to the end',
  },
  loan: {
    notFound: 'No such loan',
    apr: 'APR {rate}%',
    payments: '{count} payments from {date}',
    next: 'next {amount}, {date}',
    inPayments: { one: 'in {count} payment', other: 'in {count} payments' },
    interestWhole: '{amount} over the whole loan',
    chartTitle: 'Balance over time',
    chartLabel: 'What is still owed month by month',
    legendPaid: 'Paid',
    legendPlan: 'Plan',
    whereTitle: 'Where your payments went',
    whereLabel: 'Principal {share} of the payments made',
    principal: 'Principal (debt paid down)',
    interest: 'Interest (cost of the loan)',
    whereHint: 'From {paid} of {total} payments. Early on most of a payment is interest; over time more of it pays the debt.',
    whatIfTitle: 'What if I pay extra?',
    whatIfHint: 'Extra every month or once: how much interest you keep and what happens to the end of the loan. Check your agreement allows overpaying without a fee.',
    extraMonthly: 'Extra every month',
    extraOnce: 'Once',
    extraOnceDate: 'On',
    saved: 'Less interest',
    savedHint: 'than with the agreement’s payment',
    newEnd: 'Paid off',
    sameEnd: 'as in the agreement',
    monthsEarlier: { one: '{count} month earlier', other: '{count} months earlier' },
    newFinal: 'Final payment',
    newLast: 'Last payment',
    insteadOf: 'instead of {amount}',
    whatIfEmpty: 'Enter an amount to see what overpaying changes.',
  },
  statementImport: {
    open: 'Import from a file',
    openShort: 'Statement file',
    title: 'Import a bank statement',
    pick: 'Choose a CSV file from Lloyds',
    drop: 'or drop it here',
    how: 'In Lloyds online banking open the account, click “Export” above the transactions and save the file as CSV. A file from Halifax or Bank of Scotland works too.',
    notStatement: 'This does not look like a Lloyds statement. It needs to be a CSV file with “Transaction Date” and “Transaction Description” columns.',
    unreadable: 'The file could not be read.',
    empty: 'There are no transactions in the file.',
    anotherFile: 'Another file',
    accountMask: 'account ••{mask}',
    account: 'Into account',
    newAccount: 'New account…',
    newName: 'Account name',
    newType: 'Account type',
    owner: 'Whose',
    ownerShared: 'Shared',
    newHint: 'The opening balance comes from the statement, so the new account’s balance matches the bank straight away.',
    skipped: { one: '{count} row skipped', other: '{count} rows skipped' },
    problem: {
      DATE: 'row {line} (date)',
      AMOUNT: 'row {line} (amount)',
      OTHER_ACCOUNT: 'row {line} (another account)',
    },
    countNew: { one: '{count} new', other: '{count} new' },
    countExisting: { one: '{count} already here', other: '{count} already here' },
    rowsTitle: 'Transactions in the file',
    showNew: 'New only',
    showAll: 'All',
    dedupHint: 'What is already in the app (from the bank or an earlier import) is left unticked. When the bank later sends the same payments, it joins them with these instead of adding them again.',
    statusNew: 'New',
    statusExisting: 'Already here',
    statusMaybe: 'Probably here',
    existingAs: 'in the app: {name}, {date}',
    nothingNew: 'Everything in this file is already in the app.',
    add: { one: 'Add {count} transaction', other: 'Add {count} transactions' },
    adding: 'Adding… {done} of {total}',
    done: { one: 'Added {count} transaction from the statement', other: 'Added {count} transactions from the statement' },
  },
  forecast: {
    comingIn: 'Coming in',
    goingOut: 'Going out',
    dailySpending: 'Everyday spending per day ({currency})',
    scenarioHint: 'From tomorrow, on top of recurring payments. Left empty, it is the average of the last 90 days; enter another amount to try a different scenario. Nothing is saved.',
    invalidAmount: 'Enter a non-negative amount. The invalid amount has not been applied.',
    title: 'Balance forecast',
    currency: 'Forecast currency',
    allCash: 'All current accounts · {currency}',
    horizon: 'Forecast horizon',
    days: '{count} days',
    opening: 'Balance now',
    closing: 'Balance on {date}',
    balanceChart: 'Projected daily balance',
    minimum: 'Lowest balance:',
    shortfall: 'Your projected balance falls below zero from {date}. Review the timing of income and payments.',
    assumptions: 'An estimate based on current balances and detected recurring payments. Everyday shopping uses the average of the last 90 days; new one-off expenses are not known to the forecast. Variable amounts are approximate; balances are calculated at the end of each day. Currencies are shown separately.',
    confirmedOnly: 'Confirmed payments only',
    suggestions: 'Unconfirmed payment patterns included: {count}.',
    review: 'Review recurring payments',
    overdueNote: 'Earlier expected payments ({count}) remain listed but have not been applied to the forecast again. Review them in recurring payments.',
    schedule: 'Payments day by day',
    dayBalance: 'End of day',
  },
  upcoming: {
    empty: 'Nothing regular is due in the next 30 days',
  },
  budgets: {
    heroTitle: 'Budget · {month}',
    used: 'used',
    ofLimit: 'of {amount}',
    left: '{amount} left',
    over: 'Over by {amount}',
    perDayFor: { one: '{amount} a day for {count} day', other: '{amount} a day for {count} days' },
    projectedUnder: 'At this pace the month ends at {amount} — within budget',
    projectedOver: 'At this pace the month ends at {amount} — over budget',
    editTitle: 'Budget',
    amount: 'Per month',
    amountInvalid: 'Enter an amount above zero',
    amountMonthly: 'Amount a month ({currency})',
    amountInMonth: 'Amount in the month it applies ({currency})',
    suggestedHint: 'From your history: {amount} a month on average lately',
    when: 'When',
    whenMonthly: 'Every month',
    whenYearly: 'Once a year',
    whenMonths: 'Some months',
    whenMonth: 'Payment month',
    whenFrom: 'From',
    whenTo: 'To',
    whenMonthlyHint: 'The amount applies every month.',
    whenYearlyHint: 'The amount counts each year in this month only — it does not inflate the limit in the others.',
    whenMonthsHint: {
      one: 'The amount counts in {count} month of the year only, not in the others.',
      other: 'The amount counts in {count} months of the year, not in the others.',
    },
    whenYearlyBadge: 'yearly · {month}',
    whenIdle: 'This budget does not apply this month',
    rollover: 'Carry what is left into next month',
    rolloverHint: 'What is left adds to next month; overspending takes from it.',
    saved: 'Budget saved',
    removed: 'Budget removed',
    remove: 'Remove budget',
    emptyOverline: 'Budgets',
    emptyTitle: 'Decide how much to spend each month',
    emptyHint: 'A limit for food, shopping or everything at once — the app shows whether you are on pace and how much is left for each day.',
    fromHistory: 'Create from history',
    starterTitle: 'Budgets from your history',
    starterHint: 'Each amount is your average over the last full months, rounded up to £10. Untick what you do not want; amounts can change later.',
    starterTotal: '{amount} a month in total',
    createCount: { one: 'Create {count} budget', other: 'Create {count} budgets' },
    created: { one: '{count} budget created', other: '{count} budgets created' },
    perMonth: '{amount} / month',
    expensesTitle: 'Spending',
    incomeTitle: 'Income',
    colBudget: 'Budget',
    colSpent: 'Spent',
    colLeft: 'Left',
    colReceived: 'Received',
    editCategory: 'Edit category',
    editMain: 'Edit main category',
    hideCategory: 'Hide (stays in history)',
    settings: 'Budget settings',
    actions: 'Actions',
    subCount: { one: '{count} subcategory', other: '{count} subcategories' },
    rolloverShort: 'rest goes to next month',
    outside: '{amount} spent outside the budget (no amount set or no category)',
    planHint: 'Type the monthly amount in a category’s row. A main category with subcategories adds theirs up.',
    sharedStart: 'Always shared',
    sharedStop: 'No longer always shared',
    groupSharedStart: 'All subcategories always shared',
    groupSharedStop: 'Subcategories no longer always shared',
    wholeOn: 'One limit for the whole category',
    wholeOff: 'Separate limits in subcategories',
    wholeBadge: 'one limit',
    wholeWithin: 'in the limit',
    wholeHint: 'The main category has one limit, and all its subcategories count towards it together.',
    wholeOnToast: '“{name}” now has one limit of {amount} for all its subcategories.',
    wholeOffToast: '“{name}” has no limit of its own now: set the amounts in its subcategories.',
    sharedBadge: 'shared',
    sharedHint: 'New transactions in this category (from this month on) are shared straight away and count in the settlement without marking.',
    sharedOn: { one: '{name}: shared from now on. {count} transaction of this month marked too.', other: '{name}: shared from now on. {count} transactions of this month marked too.' },
    sharedOff: '{name}: new transactions are no longer shared automatically; those already marked stay shared.',
    add: 'Add',
    order: 'Order',
    orderDone: 'Done',
    orderHint: 'Set the order with the arrows — lists to pick from and the cash flow follow it too.',
    showIdle: { one: 'Show {count} more category — nothing set or spent', other: 'Show {count} more categories — nothing set or spent' },
    hideIdle: 'Hide categories with nothing set or spent',
  },
  overview: {
    greeting: { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' },
    greetingSub: "Here's your financial picture for {period}.",
    availableCash: 'Available cash',
    availableCashHint: 'Money on current and savings accounts — there to use now.',
    assets: 'Assets',
    assetsHint: 'Everything you own: cash, savings, investments and other things such as a car.',
    debt: 'Debt',
    debtHint: 'Cards and loans — the whole balance to repay. Only the monthly payment counts as spending.',
    debtOutside: '{amount} of it outside net worth',
    kpis: 'The period in figures',
    ofIncome: '{percent} of income',
    pendingPart: 'incl. {amount} pending',
    pendingHint: 'Payments pending at the bank: the money is blocked already — the bank will book it or give it back.',
    noIncomeYet: 'no income this period',
    safeToSpend: 'Safe to spend',
    safeHint: 'Money on current accounts after the bills, loan payments and savings transfers planned until the month ends, with the income still to come.',
    until: 'until {date}',
    perDay: '{amount} a day',
    nextPay: 'pay {date}',
    spending: 'Spending by category',
    shareOfSpending: '{percent} of spending',
    restCategories: { one: '{count} more category', other: '{count} more categories' },
    budget: 'Monthly budget',
    budgetEmpty: 'No budget yet',
    budgetEmptyHint: 'Set amounts for your categories — we suggest them from your history.',
    spentOf: '{spent} of {limit} spent',
    budgetPace: 'What an even pace would have spent by today',
    remaining: '{amount} remaining',
    overBy: '{amount} over budget',
    daysLeft: { one: '{count} day left', other: '{count} days left' },
    viewCalendar: 'View calendar',
    next14: 'The next 14 days',
    goingOut: 'Going out in the next 14 days',
    needsAttention: 'Needs attention',
    updated: 'updated {time}',
  },
  recurring: {
    title: 'Recurring',
    upcoming: 'Upcoming payments',
    upcomingEmpty: 'Nothing due in the next two weeks',
    upcomingHint: 'Recurring payments are found in your history once they repeat at least three times.',
    more: { one: 'Total, {count} more not shown', other: 'Total, {count} more not shown' },
    overdue: 'Overdue',
    awaitingBank: 'Awaiting the bank',
    dueToday: 'Due today',
    dueTomorrow: 'Due tomorrow',
    dueIn: { one: 'Due in {count} day', other: 'Due in {count} days' },
    about: 'about {amount}',
    perMonth: '{amount} / month',
    perYear: '{amount} / year',
    statSubscriptions: 'Subscriptions',
    statBills: 'Bills',
    statDebts: 'Loan repayments',
    statTotal: 'All fixed costs',
    timelineSubtitle: 'Paid and still to come',
    spotOverline: 'Fixed costs · monthly',
    spotPaid: 'Paid in the chosen period',
    spotComing: 'Still to come in the chosen period',
    inPeriod: 'In the chosen period: {amount}',
    notInPeriod: {
      one: '{count} regular payment does not fall in the chosen period',
      other: '{count} regular payments do not fall in the chosen period',
    },
    spotNext: 'Next, {date}',
    spotAction: 'Upcoming payments',
    paid: 'Paid',
    upcomingShort: 'Upcoming',
    today: 'Today',
    onDay: 'on day {day}',
    section: {
      SUBSCRIPTION: 'Subscriptions',
      BILL: 'Bills',
      DEBT: 'Loan repayments',
      OTHER: 'Other recurring payments',
      INCOME: 'Recurring income',
      TRANSFER: 'Standing transfers',
    },
    suggested: 'Found automatically',
    confirmed: 'Confirmed',
    confirm: 'Confirm',
    dismiss: 'Dismiss',
    restore: 'Restore',
    dismissedTitle: { one: 'Dismissed ({count})', other: 'Dismissed ({count})' },
    confidence: 'Confidence {value}%',
    priceUp: 'Up from {amount}',
    priceDown: 'Down from {amount}',
    ended: 'Seems to have stopped',
    repays: 'Repays {name}',
    savedConfirm: 'Marked as recurring',
    edit: 'Edit',
    editTitle: 'Recurring payment',
    name: 'Name',
    kindLabel: 'Kind',
    loan: 'Repays the loan',
    loanNone: 'None',
    saved: 'Changes saved',
    savedDismiss: 'Hidden from recurring',
    savedRestore: 'Back on the list',
    empty: 'No recurring payments found yet',
    kind: {
      SUBSCRIPTION: 'Subscription',
      BILL: 'Bill',
      DEBT: 'Loan repayment',
      INCOME: 'Income',
      TRANSFER: 'Transfer',
      OTHER: 'Recurring',
    },
    cadence: {
      WEEKLY: 'Weekly',
      FORTNIGHTLY: 'Every two weeks',
      FOUR_WEEKLY: 'Every four weeks',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      YEARLY: 'Yearly',
    },
  },
  transactions: {
    title: 'Transactions',
    empty: 'No transactions yet',
    emptyHint: 'Connect a bank account or add your first transaction.',
    emptyFiltered: 'Nothing matches these filters.',
    addManually: 'Add manually',
    merchant: 'Merchant',
    amount: 'Amount',
    date: 'Date',
    bookedOn: 'The day of the payment; the bank booked it on {date}',
    account: 'Account',
    category: 'Category',
    uncategorised: 'Uncategorised',
    kindField: 'Type',
    sender: 'From',
    recipient: 'To',
    transferTo: 'To my account',
    transferFrom: 'From my account',
    transferOutside: 'An account not in the app',
    settlementManualHint: 'Counts as neither income nor spending. Who owes whom changes only with “Mark as settled” on Settlements.',
    transferHint: 'A transfer between your own accounts counts as neither spending nor income and stays out of settlements.',
    transferPartner: 'The other side of the transfer ({date}) on that account is marked too.',
    wholeGroup: '{name}: all of it',
    paidBy: 'Paid by',
    paidFromAccount: 'From the account (its owners)',
    columnWhat: 'Transaction',
    dayTotal: 'Total',
    participants: 'For whom',
    splitMethod: 'Split',
    expenseType: 'Type',
    notes: 'Notes',
    source: 'Source',
    status: 'Status',
    count: { one: '{count} transaction', other: '{count} transactions' },
    kind: {
      EXPENSE: 'Expense',
      INCOME: 'Income',
      TRANSFER: 'Transfer',
      REFUND: 'Refund',
      SETTLEMENT_PAYMENT: 'Settlement payment',
      BALANCE_ADJUSTMENT: 'Balance adjustment',
    },
    statusValue: {
      PENDING: 'Pending',
      BOOKED: 'Booked',
      REVERSED: 'Reversed',
      CANCELLED: 'Cancelled',
    },
    sourceValue: {
      BANK: 'Bank',
      MANUAL: 'Manual',
      CSV: 'CSV import',
      OFX: 'OFX import',
      QFX: 'QFX import',
      CAMT: 'CAMT import',
      SYSTEM: 'System',
    },
    searchPlaceholder: 'Search merchant, description, note or amount',
    allAccounts: 'All accounts',
    allCategories: 'All categories',
    typeAll: 'All',
    typeExpenses: 'Spending',
    typeIncome: 'Income',
    typeTransfers: 'Transfers',
    sharedOnly: 'Shared only',
    showTransfers: 'Show own transfers',
    clearFilters: 'Clear filters',
    allDates: 'All dates',
    tabHistory: 'History',
    tabUpcoming: 'Forecast & payments',
    net: 'Net',
    netUp: 'up — more came in than went out',
    netDown: 'down — more went out than came in',
    netEven: 'broke even',
    detailsTitle: 'Transaction',
    splitSection: 'Who shares it',
    householdRule: 'Default',
    paidByAccount: 'From the account',
    splitResult: 'Split',
    deleteConfirm: 'Delete this transaction? It disappears on all devices.',
    deleted: 'Deleted',
    saveChanges: 'Save changes',
    syncState: 'Sync',
    originalDescription: 'Bank description',
    percentHint: 'Percentages must add up to 100%',
    exactHint: 'Amounts must add up to the total',
    notInSettlement: 'Not part of settlements',
    suggestion: 'Suggested: {category}',
    applyToOthers: { one: 'Also change {count} other transaction from “{merchant}”', other: 'Also change {count} other transactions from “{merchant}”' },
    appliedToOthers: { one: 'Saved — and changed on {count} other transaction', other: 'Saved — and changed on {count} other transactions' },
    uncategorisedBanner: { one: '{count} transaction without a category', other: '{count} transactions without a category' },
    knownFromHistory: 'the history suggests a category for {count}',
    fillIn: 'Fill in ({count})',
    filledIn: { one: 'Category filled in for {count} transaction', other: 'Categories filled in for {count} transactions' },
    badge: {
      pending: 'Pending',
      shared: 'Shared',
      transfer: 'Transfer',
      refund: 'Refund',
    },
    shifted: {
      badge: 'moved',
      WEEKEND: 'Due {date}, on a weekend',
      HOLIDAY: 'Due {date}, a bank holiday',
      BOTH: 'Due {date}: weekend and bank holiday',
      forMonth: 'payment for {month}',
    },
  },
  quickAdd: {
    title: 'Quick add',
    expense: 'Expense',
    income: 'Income',
    transfer: 'Transfer',
    from: 'From',
    to: 'To',
    saved: 'Saved',
    savedOffline: 'Saved on this device — will sync when you are back online',
    amountInvalid: 'Enter an amount like 12.50',
    amountTooManyDecimals: 'Too many decimal places for {currency}',
    required: 'Required',
  },
  expenseType: {
    PERSONAL: 'Personal',
    SHARED: 'Shared',
    REIMBURSABLE: 'To be paid back',
    EXCLUDED: 'Not in settlement',
  },
  splitMethod: {
    EQUAL: 'Equally',
    INCOME_PROPORTIONAL: 'By income',
    PERCENTAGES: 'By percentage',
    EXACT: 'Exact amounts',
    SHARES: 'By shares',
  },
  accounts: {
    title: 'Accounts',
    add: 'Add account',
    empty: 'No accounts yet',
    emptyHint: 'Add a manual account or connect your bank.',
    name: 'Account name',
    institution: 'Bank or institution',
    type: 'Type',
    currency: 'Currency',
    openingBalance: 'Current balance',
    owners: 'Owners',
    typeValue: {
      CURRENT: 'Current account',
      SAVINGS: 'Savings',
      CREDIT_CARD: 'Credit card',
      CASH: 'Cash',
      INVESTMENT: 'Investments',
      LOAN: 'Loan',
      MORTGAGE: 'Mortgage',
      ASSET: 'Other asset',
      LIABILITY: 'Other liability',
    },
    groupCards: 'Credit cards',
    manual: 'Manual',
    bank: 'Bank',
    netWorthHint: 'Assets minus liabilities',
    loanOwed: 'Still owed',
    loanRepaid: '{share} repaid',
    loanPayment: 'Monthly payment',
    loanNext: 'Next payment',
    loanPayoff: 'Paid off',
    loanPayoffNever: 'Payment does not cover the interest',
    loanRemaining: { one: '{count} payment left', other: '{count} payments left' },
    loanInterestLeft: 'Interest still to pay',
    loanInterestPaid: 'Interest paid so far',
    loanPrincipalPaid: 'Principal paid so far',
    loanOnDay: '{amount} on day {day}',
    loanOf: 'of {amount}',
    loanEstimate: 'Estimated from the rate and the repayments; your lender’s statement is the final word.',
    loanSuggestion: 'Looks like a loan repayment: {name}, {amount} every month.',
    loanSuggestionAction: 'Add the loan',
    formTitle: 'New account',
    formLoanTitle: 'New loan',
    owedNow: 'Amount still owed',
    principal: 'Amount borrowed',
    rate: 'Interest rate (APR, %)',
    payment: 'Monthly payment',
    paymentDay: 'Payment day',
    financeType: 'Kind of loan',
    financeTypeValue: { PERSONAL: 'Personal loan', PCP: 'Car finance — PCP', HP: 'Car finance — HP' },
    principalHint: 'Without the deposit — “Amount of credit” in the agreement',
    paymentCount: 'Number of monthly payments',
    finalPayment: 'Final payment',
    finalPaymentPcp: 'Optional final payment (GFV)',
    firstPaymentDate: 'First payment date',
    paidCount: 'Payments made',
    bankSeen: { one: 'The bank shows {count} repayment to “{name}” since {date}.', other: 'The bank shows {count} repayments to “{name}” since {date}.' },
    bankSeenHint: 'If earlier ones came from another account, enter how many payments are behind you.',
    schedulePreview: '{paid} of {total} payments made · {payable} still payable · ends {end}',
    scheduleOwedToday: 'To settle today ≈ {amount}',
    loanPayable: 'Still payable',
    loanOwedToday: 'Owed today ≈ {amount} — this counts in net worth; the rest is interest for the months ahead.',
    loanPaidCount: '{paid} of {total} payments made',
    loanFinal: 'Final payment {amount}',
    loanPcpHint: 'PCP: pay the final payment to keep the car, or hand the car back instead.',
    loanHpHint: 'HP: the car is yours after the final payment.',
    loanScheduleEstimate: 'Worked out from the agreement’s schedule and APR; your lender’s statement is the final word.',
    assetValue: 'Value today',
    assetValueHint: 'A new value is recorded as a balance adjustment dated today, so the net worth history stays true.',
    valueAdjusted: 'New value',
    loanAsset: 'In your assets: {name}',
    assetValueShort: 'Value',
    loanBalanceShort: 'To settle today',
    equity: 'Equity',
    loanAddAsset: 'Add the car to your assets',
    loanAssetHint: 'The loan counts against your net worth; the car you are paying for balances it. Enter its market value and update it now and then.',
    carName: 'Car',
    countInNetWorth: 'Count against net worth',
    countInNetWorthOn: 'On: what is owed today reduces net worth.',
    countInNetWorthOff: 'Off: net worth is your money, and the loan shows as a schedule of payments to make.',
    loanSettleToday: 'To settle today ≈ {amount}',
    loanOutside: 'Not in net worth — a schedule of payments',
    scheduleTitle: 'Payment schedule',
    scheduleShare: 'Bar and percentage: the part of what is left to repay that falls in each year',
    scheduleCount: { one: '{count} payment', other: '{count} payments' },
    scheduleFinal: 'including the final payment {amount}',
    outsideNetWorth: '+ {amount} in loans on schedule (not in net worth)',
    loanNeedsSchedule: 'Add the number of payments and the first payment date — the app then works out how many are behind you.',
    saved: 'Account added',
    nameRequired: 'Give the account a name',
    amountInvalid: 'Check the amounts',
    editTitle: 'Edit account',
    edit: 'Edit',
    changesSaved: 'Account updated',
    archivedToast: 'Account archived',
    delete: 'Delete',
    deleteForever: 'Delete for good',
    deleteConfirm: {
      one: 'Delete “{name}” with its {count} transaction? This cannot be undone.',
      other: 'Delete “{name}” with its {count} transactions? This cannot be undone.',
    },
    deleteConfirmEmpty: 'Delete “{name}”? This cannot be undone.',
    deletedToast: 'Account deleted',
    typeChangeHint: 'The bank’s entries are read again: on a savings account money in and out is a transfer, interest stays income. Changes you made by hand stay.',
    typeChangeFailed: 'The kind of account could not be changed — the server is not reachable. The other changes are saved.',
    taxWrapper: 'Tax wrapper',
    wrapperNone: 'None',
    wrapper: { ISA: 'ISA (Stocks & Shares or Cash)', LISA: 'Lifetime ISA', SIPP: 'SIPP', PENSION: 'Workplace pension' },
    wrapperShort: { ISA: 'ISA', LISA: 'LISA', SIPP: 'SIPP', PENSION: 'Pension' },
  },
  people: {
    title: 'People',
    moveUp: 'Move up',
    moveDown: 'Move down',
    add: 'Add person',
    paidShared: 'Paid for shared costs',
    share: 'Their share',
    personalSpend: 'Personal spending',
    archive: 'Archive',
    monthlyIncome: 'Monthly income',
    thisMonth: 'This month',
    you: 'You',
    incomeShare: '{share} of household income',
    noIncome: 'No income entered',
    changeIncome: 'Change income',
    setIncome: 'Enter income',
    incomeFrom: 'From',
    incomeSaved: 'Income saved',
    householdRule: 'Shared costs are split: {rule}',
    ruleIncome: 'by income ({shares})',
    ruleEqual: 'equally',
    ruleCustom: 'by the household rule',
    ruleHint: 'Changing an income changes how costs from that date on are split; earlier months stay as they were.',
    name: 'Name',
    color: 'Colour',
    personSaved: 'Person added',
    accounts: 'Accounts',
    edit: 'Edit person',
    personUpdated: 'Changes saved',
    personArchived: 'Archived — their history stays',
    archiveHint: 'People are not deleted: they appear in past costs and settlements. Archiving hides them from lists and new splits.',
    cannotArchiveOwner: 'This is you — pick someone else as “That’s me” in Settings first.',
    detectedTitle: 'Regular income found on their accounts',
    detectedPick: 'Tick the ones that count as income.',
    detectedMonthly: '{amount} / month',
    useDetected: 'Set income to {amount} / month',
    detectedMatches: 'Matches the income entered',
    detectedAverage: 'The amount is what actually came in over the last full months, on average.',
    payAccount: 'Pay arrives on',
    loans: 'Loans and cards',
    payAccountAuto: 'Find it (accounts only this person owns)',
  },
  settlement: {
    why: 'Why this amount',
    whySince: 'Shared costs since {date}: paid − share',
    whyAll: 'All shared costs so far: paid − share',
    whyPeriod: 'Shared costs of {period}: paid − share',
    itemPending: 'pending at the bank',
    scope: 'What to settle',
    scopePeriod: 'Period: {period}',
    scopeAll: 'Everything not settled',
    scopePeriodHint: 'Shared costs of the period chosen above, less those already in a settlement.',
    scopeAllHint: 'Every shared cost since the last settlement, from any period.',
    whyLegend: 'Paid for shared costs minus each person’s share (plus settlements already made). Whoever is below zero pays the difference.',
    celebrate: 'Settled — everyone is even',
    title: 'Settlements',
    allSettled: 'All settled up',
    allSettledHint: 'Nobody owes anybody anything right now.',
    sharedSoFar: 'Shared costs',
    since: 'Counted since {date}',
    neverSettled: 'nothing settled yet',
    paidVsShare: 'paid {paid} · share {share}',
    settleNow: 'Settle up',
    open: 'Open settlements',
    transfer: '{from} → {to}',
    markSettled: 'Mark as settled',
    paidWith: 'Transfer {from} → {to} ({amount}) in the bank',
    paidWithNone: 'Not linked to a transfer',
    paidWithNothingFound: 'No matching transfer in the last 45 days',
    note: 'Note',
    notePlaceholder: 'e.g. paid in cash, for September',
    notePresets: {
      transfer: 'Paid back by transfer for {month}',
      cash: 'Paid back in cash for {month}',
      shopping: 'Evened out with shopping for {month}',
    },
    notePresetLabels: { transfer: 'Transfer', cash: 'Cash', shopping: 'Shopping' },
    pendingWarning: {
      one: '{count} payment is still pending at the bank — the amount may change.',
      other: '{count} payments are still pending at the bank — the amount may change.',
    },
    autoNote: 'Paid back by transfer for {month} (settled automatically)',
    paidOn: 'transfer {date}',
    undo: 'Undo settlement',
    history: 'Settlement history',
    youOweName: 'You owe {name}',
    owesYouName: '{name} owes you',
    owesName: '{from} owes {to}',
    confirmQuestion: 'Record this as paid back today? The balance goes to zero; you can undo it.',
    confirm: 'Yes, settled',
    settledToast: 'Settled',
    undoneToast: 'Settlement undone',
    personPaid: 'Paid for shared costs',
    personShare: 'Their share',
    personSettled: 'Evened out by transfers',
    netPositive: 'Is owed {amount}',
    netNegative: 'Owes {amount}',
    netZero: 'Even',
    itemsTitle: 'Since the last settlement',
    itemsAll: 'Shared costs so far',
    itemsEmpty: 'No shared costs since the last settlement.',
    itemPaidBy: 'paid by {name}',
    moreItems: { one: '{count} more', other: '{count} more' },
    historyEmpty: 'No settlements yet.',
    statusSettled: 'Settled',
    statusUndone: 'Undone',
    lastSettled: 'Last settled {date}',
    reviewTitle: 'Needs a decision',
    howItWorks: 'Everything paid for shared costs, minus each person’s share, plus transfers that evened things out.',
    needsReview: {
      one: '{count} transaction needs a decision before it can be settled',
      other: '{count} transactions need a decision before they can be settled',
    },
    issue: {
      NO_PAYER: 'Nobody is set as the payer',
      NO_PARTICIPANTS: 'Nobody is set as sharing this cost',
      INCOME_MISSING: 'Split by income, but no income is set — split equally for now',
      RATE_MISSING: 'Missing exchange rate for an income',
      INVALID_PERCENTAGES: 'Percentages do not add up to 100%',
      INVALID_EXACT: 'Amounts do not add up to the total',
      INVALID_SHARES: 'Shares must be positive',
      INVALID_FUNDING: 'Account funding rule does not add up to 100%',
      PERSONAL_WITHOUT_BENEFICIARY: 'Personal expense on a joint account — choose whose it is',
      REFUND_ORIGINAL_MISSING: 'The refunded transaction was not found',
    },
  },
  savings: {
    title: 'Savings & investments',
    totalValue: 'Total value',
    putAsideThisMonth: 'Put aside this month',
    putAsideTaxYear: 'Put aside in tax year {year}',
    savingsRate: 'Savings rate this month',
    isaTitle: 'ISA allowance {year}',
    isaUsed: '{used} of {limit} paid in',
    isaLeft: '{amount} left',
    isaDeadline: { one: 'until 5 April — {count} day left', other: 'until 5 April — {count} days left' },
    isaLisa: 'of which Lifetime ISA: {amount} (limit {limit})',
    isaHint: 'Mark your ISA accounts (pencil on the Accounts screen) and the yearly allowance is tracked here.',
    isaFlexibleNote: 'Counted as Trading 212 does: its Stocks ISA is flexible, so money taken out in the same tax year gives the allowance back. In other ISAs it does not.',
    monthlyTitle: 'Put aside each month',
    accountsTitle: 'Savings and investment accounts',
    paidInThisYear: 'Paid in this tax year: {amount}',
  },
  salary: {
    title: "Salary calculator",
    takeHome: "Take-home a year",
    takeHomeWeekly: "Take-home a week",
    effective: "Tax and NI together",
    inputs: "From rate to take-home",
    note: "UK 2026/27. Income tax 20% to £50,270, 40% to £125,140, 45% above. Employee NI 8% to £50,270, 2% above. Pension is taken from base pay and bonus (no overtime), before tax. An approximation.",
    baseRate: "Hourly rate",
    baseHoursLabel: "Base hours a week",
    overtime: "Overtime",
    bonus: "Annual bonus (total)",
    taxes: "Tax, NI and pension",
    pension: "Employee pension contribution",
    allowanceTax: "Personal allowance (tax)",
    allowanceNI: "Primary threshold (NI)",
    breakdown: "Pay breakdown",
    weekly: "Weekly",
    yearly: "Yearly",
    baseHours: "Base hours",
    weekendOvertime: "Weekend overtime",
    bonusRow: "Bonus",
    pensionRow: "Pension ({pct}%)",
    taxable: "Taxable income",
    incomeTax: "Income tax",
    allowanceRow: "Personal allowance 0% (up to {amount})",
    basic: "Basic rate 20%",
    higher: "Higher rate 40%",
    additional: "Additional rate 45%",
    totalTax: "Total income tax",
    ni: "National Insurance",
    ni0: "NI 0% (up to {amount})",
    ni8: "NI 8% ({from}–{to})",
    ni2: "NI 2% (above {amount})",
    totalNi: "Total National Insurance",
    net: "Take-home",
    multiplier: "Multiplier",
    hoursPerWeek: "Hours a week",
    takeHomeMonthly: "Take-home a month",
    legend: {
      net: "Net",
      tax: "Tax",
      ni: "NI",
      pension: "Pension",
    },
    showWeeks: "Enter weeks",
    hideWeeks: "Collapse",
    grossRow: "Gross",
    monthly: "Monthly",
    showDetails: "Show details",
    hideDetails: "Hide details",
    weeksTitle: "Actual pay: 52 weeks",
    weeksHint: "Enter the take-home amount from your payslip. The difference by each week compares it with the theoretical weekly pay above. Weeks at 0 do not count. Kept on this device.",
    filled: "Entered",
    total: "Total",
    average: "Average",
    min: "Min",
    max: "Max",
    result: "Profit or loss",
    weekOne: "Week 1 Friday",
    clear: "Clear all",
    clearConfirm: "Clear all 52 weekly pays?",
    week: "Week {n}",
    compareTitle: "Comparison",
    theory: "Theoretical forecast",
    theorySub: "take-home a year",
    entered: "Sum of entered weeks",
    enteredSub: "{count} of 52 weeks, take-home",
    actual: "Forecast from actual pay",
    actualSub: "typical (median) week × 52",
    progress: "{pct}% of the year filled in ({count} of 52 weeks)",
    bannerEmpty: "Enter a few weekly pays to see the comparison.",
    higherText: "Based on {count} entered weeks, your take-home is higher than the theoretical forecast ({pct}%).",
    lowerText: "Based on {count} entered weeks, your take-home is lower than the theoretical forecast ({pct}%).",
  },
  simulation: {
    title: "Simulation",
    yearTotal: "Total in {year}",
    monthAverage: "Monthly average",
    billsCounted: "Bills counted",
    year: "Year",
    billsTitle: "Bills",
    billsHint: "Bills counted over the whole year. Kept on this device only.",
    addBill: "Add a bill",
    empty: "No bills",
    emptyHint: "Add the first bill and the whole-year total appears below.",
    on: "Count this bill",
    name: "Name",
    namePlaceholder: "e.g. Electricity",
    amount: "Amount",
    frequency: "How often",
    monthly: "Every month",
    quarterly: "Every quarter",
    yearly: "Once a year",
    inMonth: "In which month",
    firstMonth: "First month",
    day: "Day",
    who: "Who pays",
    perYear: "Per year",
    remove: "Remove bill",
    byPerson: "Split by person",
    byPersonHint: "Each bill is divided equally between the people ticked.",
    monthsTitle: "Month by month, {year}",
    calendarTitle: "The year in a table",
    month: "Month",
    takenOn: "Taken on",
    monthlyShort: "a month",
    rate: "You keep",
    rateHint: "of income is left after the bills",
    quickAdd: "Quick add",
    duplicate: "Repeat this bill",
    fromSalary: "Use the salary calculator’s take-home ({amount} a year)",
    rankTitle: "Where the money goes",
    rankHint: "Bills from the largest, with their share of the total.",
    rankHintIncome: "Bills from the largest, with their share of income.",
    unnamed: "Unnamed",
    preset: {
      rent: "Rent",
      councilTax: "Council Tax",
      electricity: "Electricity",
      gas: "Gas",
      water: "Water",
      internet: "Internet",
      phone: "Phone",
      carInsurance: "Car insurance",
      fuel: "Fuel",
      food: "Food",
    },
    incomeYear: 'Income in {year}',
    savedYear: 'Left at the end of {year}',
    incomeTitle: 'Income',
    incomeHint: 'What each person takes home. The bills come off it to work out what is left.',
    when: 'When',
    addPerson: 'Add a person',
    removePerson: 'Remove person',
    personName: 'Name',
    personFallback: 'Person {n}',
    incomeAmount: 'Take-home income',
    incomePer: 'How often',
    per: { week: 'Weekly', month: 'Monthly', year: 'Yearly' },
    noPeople: 'No people',
    noPeopleHint: 'Add a person to enter their income and pick them on the bills.',
    addPeopleFirst: 'Add a person under income first.',
    weekly: 'Every week',
    weekday: 'Day of the week',
    incomeShort: 'Income',
    billsShort: 'Bills',
    left: 'Left',
    builtUp: 'Built up',
    savedTitle: 'What is left each month, {year}',
    yearOnly: 'All of {year}',
    total: "Total",
  },
  netWorth: {
    title: 'Net worth',
    assets: 'Assets',
    liabilities: 'Liabilities',
    groupCash: 'Cash & current accounts',
    groupSavings: 'Savings',
    groupInvestments: 'Investments',
    groupOther: 'Other assets',
    columnName: 'Name',
    columnWeight: 'Share',
    columnValue: 'Value',
    missingRate: { one: '{count} account without an exchange rate is left out', other: '{count} accounts without an exchange rate are left out' },
    rangeShort: { '1M': '1M', '3M': '3M', '6M': '6M', YTD: 'YTD', '1Y': '1Y', '5Y': '5Y', ALL: 'All' },
    since: 'since {date}',
    chartLabel: 'Net worth over time',
    ratesNote: 'Foreign currencies at today’s rates.',
    whose: 'Whose net worth',
    everyone: 'Everyone',
    byPerson: 'By person',
    unassigned: 'No owner',
    jointNote: 'An account counts for its owners; a joint one in equal parts.',
  },
  integrations: {
    connectionsTitle: 'Investment connections',
    readOnly: 'Read-only',
    accessMode: 'Access',
    accountKind: 'Account type',
    refreshNow: 'Refresh now',
    disconnect: 'Disconnect',
    apiKey: 'API key',
    apiSecret: 'API secret',
    needsKeyAndSecret: 'Fill in both the API key and the API secret. Trading 212 shows the secret only once, when the key is created, so if you do not have it, create a new key.',
    connectHint: 'Generate a read-only key in the Trading 212 app. It is stored encrypted on the server and never on this device.',
    status: {
      CONNECTED: 'Connected',
      SYNCING: 'Syncing…',
      ACTION_REQUIRED: 'Action required',
      CONSENT_EXPIRING: 'Access expires soon',
      EXPIRED: 'Access expired',
      CONSENT_EXPIRED: 'Access to be confirmed again',
      RATE_LIMITED: 'Bank limit — reading later',
      ERROR: 'Sync problem',
      DISCONNECTED: 'Disconnected',
      UNSUPPORTED: 'Account type not supported',
    },
    freshness: {
      syncedAgo: 'Synced {time}',
    },
    connect: 'Connect',
    connectTitle: 'Connect Trading 212',
    connectDemo: 'Connect a test account (no key)',
    environment: 'Environment',
    environmentLive: 'Real account',
    environmentDemo: 'Trading 212 demo account',
    kindInvest: 'Invest',
    kindIsa: 'Stocks ISA',
    linkAccount: 'Account in this app',
    linkNew: 'Create a new one',
    connected: 'Connected to Trading 212',
    disconnected: 'Disconnected — the key was deleted from the server',
    refreshed: 'Refreshed',
    keyHint: 'Key …{hint}',
    error: {
      INVESTMENT_UNAUTHORIZED: 'Trading 212 did not accept the key or secret. Check they were copied in full.',
      INVESTMENT_FORBIDDEN: 'The key cannot read the account, portfolio and history. Generate one with those permissions.',
      INVESTMENT_RATE_LIMITED: 'Trading 212 asks for a short break. Try again in a minute.',
      INVESTMENT_UNAVAILABLE: 'Trading 212 could not be reached. Try again later.',
      NOT_CONFIGURED: 'Investment connections are not enabled on this server yet.',
      generic: 'Could not connect.',
    },
    positionsTitle: 'Positions',
    columnInstrument: 'Instrument',
    columnQuantity: 'Quantity',
    columnAverage: 'Avg price',
    columnPrice: 'Price',
    columnValue: 'Value',
    columnResult: 'Result',
    inPie: 'in a pie',
    cashInPies: 'Cash in pies',
    result: 'Result',
    resultHint: 'value minus what you paid in',
    paidIn: 'Paid in so far',
    value: 'Value',
    noDividends: 'Accumulating funds — dividends stay in the fund and lift the unit price.',
  },
  palette: {
    title: 'Command palette',
    placeholder: 'Search transactions, pages and actions…',
    actions: 'Actions',
    pages: 'Go to',
    transactions: 'Transactions',
    empty: 'Nothing matches “{query}”.',
    hintMove: 'move',
    hintOpen: 'open',
    hintClose: 'close',
    addExpense: 'Add expense',
    addIncome: 'Add income',
    addTransfer: 'Transfer between accounts',
    connectBank: 'Connect a bank',
    refreshBanks: 'Refresh bank connections',
    refreshInvestments: 'Refresh Trading 212',
    connectInvestments: 'Connect Trading 212',
    themeDark: 'Switch to dark mode',
    themeLight: 'Switch to light mode',
    privacyOn: 'Hide amounts',
    privacyOff: 'Show amounts',
    languageSwitch: 'Polski',
    refreshed: 'Refreshed',
  },
  banks: {
    connect: 'Connect a bank',
    connectShort: 'Connect bank',
    connectTitle: 'Connect a bank',
    connectHint: 'You sign in on your bank’s own page and allow read-only access to accounts, balances and transactions. This app never sees your password and cannot make payments.',
    country: 'Country',
    countryGB: 'United Kingdom',
    countryPL: 'Poland',
    search: 'Search banks',
    noBanks: 'No banks match.',
    testBanks: 'Test banks with sample data — no real bank is contacted.',
    investmentsElsewhere: 'Trading 212 is connected separately, with a read-only API key:',
    investmentsLink: 'Savings & investments →',
    opening: 'Opening {bank}…',
    title: 'Bank connections',
    accountsCount: { one: '{count} account', other: '{count} accounts' },
    consentUntil: 'access until {date}',
    lastRun: 'Last download: {inserted} new, {paired} transfers recognised',
    details: 'Details',
    chooseAccounts: 'Choose accounts',
    pendingBanner: '{bank} reported accounts you have not decided about yet.',
    refresh: 'Refresh',
    reconnect: 'Connect again',
    disconnect: 'Disconnect',
    disconnected: 'Disconnected — the accounts and their history stay, kept by hand from now on',
    refreshed: 'Up to date · {count} new',
    expiredBanner: '{bank}: access has ended. To keep getting new transactions, connect again.',
    reauthBanner: 'Access needs to be confirmed again. Your history stays.',
    nextRead: 'next read {time}',
    alreadyFollowed: 'Already followed as “{name}”',
    expiringBanner: '{bank}: access ends on {date}. Connect again to keep it going.',
    errorBanner: '{bank}: the last download failed. Try refreshing.',
    chooseTitle: 'Accounts at {bank}',
    chooseHint: 'Choose which accounts to follow and whose they are. A joint account has two owners.',
    include: 'Follow this account',
    becomes: 'In this app',
    asNew: 'New account',
    linkTo: 'Replace “{name}”',
    linkHint: 'Its history stays; the bank continues from the day after its last entry.',
    typeHint: 'Choose the kind of account now — it cannot be changed later.',
    lunchFlow: {
      hint: 'Your banks are connected in Lunch Flow. Here you choose which of the accounts it shares with this app to follow.',
      connected: 'Synced',
      syncing: 'Syncing…',
      stale: 'Data may be out of date',
      reauthorizationRequired: 'Reconnection required',
      unavailable: 'Lunch Flow temporarily unavailable',
      lastSync: 'read {time}',
      schedule: 'The app reads Lunch Flow every hour, and Lunch Flow reads the bank a few times a day (it does not say when) — a card payment may take a few hours to arrive. “Refresh now” fetches what Lunch Flow already has.',
      syncNow: 'Refresh now',
      syncNowHint: 'Fetch the latest data available in Lunch Flow',
      synced: 'Synced with Lunch Flow just now · new: {count}',
      openLunchFlow: 'Open Lunch Flow',
      reauthBanner: 'The {bank} connection needs to be confirmed again. Open Lunch Flow and reconnect the account.',
      unavailableBanner: 'Lunch Flow is temporarily unavailable. We will try again at the next sync.',
      expiring: 'Bank access expires soon',
      renewBy: 'renew in Lunch Flow by about {date}',
      expiringBanner: '{bank} access in Lunch Flow ends around {date} (the bank asks every 90 days). Renew it in Lunch Flow, then click “Renewed”.',
      renewed: 'Renewed',
      renewedDone: 'Renewal saved. The next reminder comes before {date}.',
      accountActive: 'active',
      accountReconnect: 'needs reconnecting',
      accountTrouble: 'Lunch Flow reports a problem',
      accountHidden: 'no longer shared in Lunch Flow',
    },
    save: 'Save and download',
    importing: 'Downloading history from {bank}…',
    added: { one: '{count} account added', other: '{count} accounts added' },
    nothingChosen: 'Nothing chosen — you can come back to it from Accounts.',
    waiting: 'Getting the account list…',
    errorDenied: 'Connecting was cancelled at the bank.',
    errorFailed: 'The bank did not confirm access. Try again.',
    error: {
      BANK_UNAVAILABLE: 'The bank could not be reached. Try again later.',
      BANK_PROVIDER_UNAVAILABLE: 'Connecting banks is not available on this server yet.',
      BANK_CONSENT_EXPIRED: 'Access has ended — connect the bank again.',
      BANK_REFRESH_TOO_SOON: 'Refreshed a moment ago — try again in a minute.',
      BANK_RATE_LIMITED: 'The bank allows only a few reads for now; the app will try again later.',
      BANK_ACCOUNT_ALREADY_LINKED: 'This account is already followed through another connection to this bank.',
      BANK_ALREADY_CONNECTED: 'This connection already exists — new accounts are added to it.',
      BANK_SYNC_IN_PROGRESS: 'A sync is already running — the data will appear in a moment.',
      BANK_UNAUTHORIZED: 'The bank data provider rejected the access key. Check the server configuration.',
      BANK_FORBIDDEN: 'The bank data provider refused access (for example, the subscription).',
      BANK_NOT_FOUND: 'The provider no longer knows this account.',
      BANK_BAD_RESPONSE: 'The bank data provider answered unexpectedly. Try again later.',
      NOT_CONFIGURED: 'Connecting banks is not available on this server yet.',
      generic: 'Could not connect the bank.',
    },
    review: {
      badge: 'To check',
      duplicateTitle: 'Possibly entered twice',
      duplicateText: 'You typed in {amount} at {merchant} on {date}. Is it the same payment the bank sent?',
      merge: 'Same payment — keep the bank’s',
      different: 'Different payments',
      merged: 'Merged — your category and split were kept',
      kept: 'Both kept',
      transferTitle: 'Looks like a transfer',
      transferText: 'The same amount went the other way on {account} on {date}.',
      transferMark: 'Mark as a transfer',
      transferMarked: 'Marked as a transfer between your accounts',
      pending: 'Pending — the bank has not booked it yet; the amount may still change.',
    },
  },
  notifications: {
    title: 'Notifications',
    unread: { one: '{count} new notification', other: '{count} new notifications' },
    empty: 'Nothing new. You will see here what needs your attention.',
    markAllRead: 'Mark all as read',
    hide: 'Hide',
    settingsLink: 'Notification settings',
    budgetOver: 'Budget “{name}” is over: {spent} of {limit}',
    budgetOverAll: 'The month’s budget is over: {spent} of {limit}',
    budgetNear: 'Budget “{name}” is nearly used up: {spent} of {limit}',
    budgetNearAll: 'The month’s budget is nearly used up: {spent} of {limit}',
    paymentLate: '{name}: the payment has not gone out (due {date})',
    incomeLate: '{name}: the money has not come in (due {date})',
    paymentHigher: '{name}: {amount} instead of the usual {usual}',
    largeExpense: 'Large expense: {name}, {amount}',
    settleUp: 'Settle up for {month}: {from} → {to} {amount}',
    settled: 'Settled for {month}: the transfer {from} → {to} {amount} came in and was recorded by itself',
    bankRenew: '{bank} access in Lunch Flow ends around {date}',
    bankReconnect: '{bank}: connect again',
    settings: {
      budgets: 'Budgets',
      budgetsHint: 'At 90% and when a budget is over',
      payments: 'Regular payments',
      paymentsHint: 'When one does not come on time or comes clearly higher',
      large: 'Large expenses',
      largeHint: 'A single expense from this amount',
      settlement: 'Settling up',
      settlementHint: 'In the first days of a month, while the one before is not settled',
      banks: 'Banks',
      banksHint: 'Renewing access and connection problems',
      system: 'System notifications',
      systemHint: 'Also as Windows or browser notifications while the app is open',
      systemBlocked: 'The browser blocks notifications for this page — allow them in its site settings.',
      systemUnsupported: 'This browser does not show notifications.',
    },
  },
  sync: {
    title: 'Sync',
    synced: 'Synced',
    pending: 'Waiting to sync',
    syncing: 'Syncing',
    failed: 'Sync error',
    conflict: 'Conflict needs a decision',
    offline: 'Offline — changes are saved on this device',
    lastSuccess: 'Last successful sync {time}',
    pendingOperations: { one: '{count} change waiting', other: '{count} changes waiting' },
    failedOperations: { one: '{count} change failed', other: '{count} changes failed' },
    retry: 'Retry',
    fullResync: 'Full resync',
    devices: 'Devices',
    signOutDevice: 'Sign out this device',
    thisDevice: 'This device',
    conflictTitle: 'This transaction was changed on another device',
    serverVersion: 'Version on the server',
    yourVersion: 'Your version',
    useServer: 'Use server version',
    useMine: 'Use my version',
    compareFields: 'Compare fields',
  },
  settings: {
    title: 'Settings',
    household: 'Household',
    appearance: 'Appearance',
    language: 'Language',
    sync: 'Sync',
    theme: 'Theme',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    privacyMode: 'Hide amounts',
    privacyHint: 'Amounts show as dots — handy on a shared screen.',
    cashEntry: 'Till-style amounts',
    cashEntryHint: 'Digits come in from the right: 12568 is 125.68, and 120 is 1.20. This device only.',
    devices: {
      title: 'Devices',
      subtitle: 'Where you use the app with this account.',
      thisDevice: 'This device',
      seen: 'last used {last} · since {first}',
      forget: 'Forget',
      forgotten: 'Forgotten: {name}',
      empty: 'The list appears after the first sync.',
      showAll: 'Show all ({count})',
      showFewer: 'Show fewer',
    },
    categories: {
      newTitle: 'New category',
      editTitle: 'Category',
      name: 'Name',
      kind: 'Kind',
      icon: 'Icon',
      color: 'Colour',
      colorFromGroup: 'A subcategory is shown in a shade of its main category’s colour, the same on every screen.',
      defaultName: 'Default name: {name}',
      restoreName: 'Restore',
    groupHint: 'Subcategories of one main category add up in Cash flow and in the budget, and appear under its name when you pick one.',
    groupNewTitle: 'New main category',
    groupEditTitle: 'Main category',
    groupName: 'Name',
    groupPlaceholder: 'e.g. Car or Bills',
    groupSaved: 'Main category saved',
    groupRemove: 'Remove main category',
    groupRemoved: 'Main category removed — its subcategories are main categories now',
      restore: 'Restore',
      hidden: 'Hidden',
      nameRequired: 'Give the category a name',
      saved: 'Category saved',
      hiddenToast: 'Category hidden — transactions keep it',
      restoredToast: 'Category restored',
      place: 'Where',
      placeMain: 'A main category',
      placeSub: 'A subcategory of',
      delete: 'Delete category',
      deleting: 'Deleting…',
      deleteTitle: 'Delete {name}',
      deleteMainTitle: 'Delete main category {name}',
      deleteUsed: { one: '{name} is on {count} transaction. Choose where it goes; the category then disappears for good.', other: '{name} is on {count} transactions. Choose where they go; the category then disappears for good.' },
      deleteUnused: 'No transaction has {name}: it disappears for good.',
      moveTo: 'Move its transactions to',
      deleted: '{name} deleted',
      deleteMain: { one: 'Its {count} subcategory becomes a main category; transactions stay as they are. Delete a subcategory from its own “…”.', other: 'Its {count} subcategories become main categories; transactions stay as they are. Delete a subcategory from its own “…”.' },
      deleteMainEmpty: '{name} has no subcategories: it disappears for good.',
    },
    account: 'Account',
    signedInAs: 'Signed in as {email}',
    password: 'Password',
    passwordHint: 'Afterwards your other devices, such as the phone, are signed out. Sign in there with the new password.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password (at least 10 characters)',
    repeatPassword: 'Repeat the new password',
    passwordSaving: 'Changing…',
    passwordTooShort: 'The new password needs at least 10 characters.',
    passwordMismatch: 'The repeated password is not the same.',
    passwordSame: 'The new password must differ from the current one.',
    passwordWrong: 'The current password is not right.',
    passwordWeak: 'This password is too weak. Choose a longer or less obvious one.',
    passwordChanged: 'Password changed. Sign in with the new one on your other devices.',
    householdName: 'Household name',
    householdSwitchHint: 'Your account has more than one household; this device shows the one chosen here.',
    baseCurrency: 'Base currency',
    ownerPerson: 'That’s me',
    ownerPersonHint: 'Decides whose point of view “you owe” and “owes you” use.',
    countFrom: 'Count from',
    countFromHint:
      'Transactions before this day neither count nor show — not in spending, income, budgets or settlements — and nothing older is downloaded from the bank. They stay stored: an earlier date brings them back. Recurring payments are still found in the earlier months, and investments count as before.',
    countFromAll: 'Count everything',
    countFromSaved: 'Counting from {date}. Devices refresh their data by themselves.',
    countFromCleared: 'Everything counts. Devices refresh their data by themselves.',
    saved: 'Saved',
    syncNow: 'Sync now',
    state: 'State',
    lastSync: 'Last successful sync',
    never: 'Not yet',
    queued: 'Changes waiting',
    failed: 'Changes that failed',
    failedHint: 'The server refused these changes for good — usually because it ran an older version than the app. “Retry” sends them all again; “Discard” keeps what the server holds.',
    failedAction: { create: 'added', update: 'changed', delete: 'deleted' },
    failedReason: {
      SETTLEMENT_MISMATCH: 'The balances changed before the settlement reached the server (a bank payment came in, for example). Discard it and settle again.',
      VALIDATION: 'The server did not accept this data',
      NOT_FOUND: 'This record is no longer on the server',
      REFERENCE_NOT_FOUND: 'The account, category or person it refers to is not on the server',
      ID_TAKEN: 'This record is already on the server',
      IMMUTABLE_FIELD: 'This field cannot change',
      READ_ONLY_ENTITY: 'This cannot be changed',
      FORBIDDEN: 'Not allowed to make this change',
    },
    failedEntity: {
      household: 'Household',
      person: 'Person',
      account: 'Account',
      categoryGroup: 'Main category',
      category: 'Category',
      tag: 'Tag',
      transaction: 'Transaction',
      incomePeriod: 'Income',
      settlementRule: 'Split rule',
      settlement: 'Settlement',
      recurringPattern: 'Recurring payment',
      investmentConnection: 'Investment connection',
      investmentPortfolio: 'Portfolio',
      investmentEvent: 'Portfolio event',
      bankConnection: 'Bank connection',
      budget: 'Budget',
      savingsGoal: 'Savings goal',
    },
    discard: 'Discard',
    discardAll: 'Discard all',
    conflicts: 'Conflicts',
    resyncHint: 'Downloads everything from the server again; changes waiting on this device are kept.',
    lastError: 'Last error: {message}',
  },
  errors: {
    network: 'No connection. Your changes are safe on this device.',
    validation: 'Please check the highlighted fields.',
  },
  categories: {
    housing: 'Housing',
    groceries: 'Groceries',
    restaurants: 'Restaurants',
    transport: 'Transport',
    shopping: 'Shopping',
    entertainment: 'Entertainment',
    health: 'Health',
    bills: 'Bills',
    subscriptions: 'Subscriptions',
    travel: 'Travel',
    children: 'Children',
    pets: 'Pets',
    taxes: 'Taxes',
    gifts: 'Gifts',
    education: 'Education',
    salary: 'Salary',
    other: 'Other',
  },
} as const;
